import type { ChangeEventHandler } from 'react';
import type { DropdownChangeFn } from '../Dropdown/Dropdown';
import type { RenderMediaGridDataFn } from '../MediaGrid/MediaGrid';

import { use, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getTestProps } from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import {
  useContentType,
  useContentTypes,
  useDefinitionsCount,
} from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import { useFeaturedImages } from '../../hooks/api/useFeaturedImages';
import useDebounceCallback from '../../hooks/useDebounceCallback';

import { ModalInstanceContext } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import Dropdown from '../Dropdown/Dropdown';
import Heading from '../Heading/Heading';
import Input from '../Input/Input';
import MediaGrid from '../MediaGrid/MediaGrid';
import PickCard from '../PickCard/PickCard';

interface PickTypeContentModalProps {
  /**
   * Type of relation to filter content types by.
   */
  relationType?: string;
  /**
   * Component test id
   */
  testId?: string;
}

const PickTypeContentModal = ({
  relationType = '',
  testId,
}: PickTypeContentModalProps) => {
  const { t } = useTranslation();
  const { permissions } = use(UserContext);
  const modalInstance = use(ModalInstanceContext);

  const COsWithCreate = useMemo(
    () =>
      permissions.getCtdNamesWithPermission(
        'CO',
        RolePermissions.PERMISSIONS_TYPES.CREATE,
      ),
    [permissions],
  );

  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [queryBy, setQueryBy] = useState('label');

  const ctdsParams = useMemo(
    () => ({
      limit: 10,
      order_by: 'label',
      order_direction: 'asc',
      page,
      internal: false,
      [queryBy]: query,
      ...(COsWithCreate ? { names: COsWithCreate } : {}),
    }),
    [COsWithCreate, page, query, queryBy],
  );

  const ctdsHookOptions = useMemo(
    () => ({
      pause: !!relationType,
    }),
    [relationType],
  );

  const {
    data: contentTypes,
    isLoading: contentTypesAreLoading,
    errors: contentTypesErrors,
    pagination: contentTypesPagination,
  } = useContentTypes(ctdsParams, ctdsHookOptions);

  const ctdHookOptions = useMemo(
    () => ({
      pause: !relationType,
    }),
    [relationType],
  );

  const {
    entity: contentType,
    isLoading: contentTypeIsLoading,
    errors: contentTypeErrors,
  } = useContentType(relationType, undefined, ctdHookOptions);

  const finalContentTypes = useMemo(() => {
    if (relationType) return contentType ? [contentType] : [];
    return contentTypes;
  }, [relationType, contentType, contentTypes]);

  const ctdCountsParams = useMemo(
    () => ({
      content_type: relationType
        ? [relationType]
        : finalContentTypes?.map((element) => element.name),
    }),
    [finalContentTypes, relationType],
  );

  const ctdCountsOptions = useMemo(
    () => ({
      pause: !relationType && contentTypesAreLoading,
    }),
    [contentTypesAreLoading, relationType],
  );

  const {
    entity: ctdCounts,
    isLoading: ctdCountsAreLoading,
    errors: ctdCountsErrors,
  } = useDefinitionsCount(ctdCountsParams, ctdCountsOptions);

  useApiErrorsToast(contentTypesErrors);
  useApiErrorsToast(contentTypeErrors);
  useApiErrorsToast(ctdCountsErrors);

  const setSearchBy = useCallback<DropdownChangeFn>((_, value) => {
    setQueryBy(value);
    setPage(1);
  }, []);

  const setFilterQuery = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      setQuery(event.target.value);
      setPage(1);
    },
    [],
  );

  const handleQueryChange = useDebounceCallback(setFilterQuery, 500);

  const [featuredImages, featuredImagesLoading] =
    useFeaturedImages(finalContentTypes);

  const renderCard = useCallback<RenderMediaGridDataFn>(
    (ctdData) => (
      <PickCard
        key={ctdData.id}
        onClick={() => modalInstance.resolve?.(ctdData.name)}
        title={ctdData.label}
        description={t('TypeDefinitionCard.ObjectNumber', {
          count: ctdCounts?.data?.[ctdData.name] || 0,
        })}
        imageUrl={featuredImages[ctdData.name]}
        imageUrlLoading={featuredImagesLoading}
        {...getTestProps(testId, ctdData.name, 'testId')}
      />
    ),
    [
      ctdCounts,
      featuredImages,
      featuredImagesLoading,
      modalInstance,
      t,
      testId,
    ],
  );

  const searchByOptions = useMemo(
    () => [
      { value: 'label', label: t('ContentDefinition.Label') },
      { value: 'name', label: t('ContentDefinition.Name') },
    ],
    [t],
  );

  const filtersNode = useMemo(() => {
    if (relationType) return null;
    return (
      <div className="flex flex-col xs:flex-row gap-2">
        <Input
          placeholder={t('ContentDefinition.Search')}
          type="search"
          onChange={handleQueryChange}
          value={query}
          autoFocus
        />
        <Dropdown
          options={searchByOptions}
          onChange={setSearchBy}
          value={queryBy}
          additionalOptionsClasses="py-1.5!"
          additionalClasses="w-full xs:w-1/3"
          {...getTestProps(testId, 'search-by', 'testId')}
        />
      </div>
    );
  }, [
    relationType,
    t,
    handleQueryChange,
    query,
    searchByOptions,
    setSearchBy,
    queryBy,
    testId,
  ]);

  return (
    <MediaGrid
      data={finalContentTypes}
      page={page}
      renderMedia={renderCard}
      isLoading={
        (relationType ? contentTypeIsLoading : contentTypesAreLoading) ||
        ctdCountsAreLoading
      }
      errors={contentTypeErrors || contentTypesErrors}
      pagination={
        relationType
          ? {
              total_pages: 1,
              count: 1,
              total_count: 1,
              current_page: 1,
            }
          : contentTypesPagination
      }
      handlePageChange={setPage}
      areFiltersApplied={!!query}
      filtersNode={filtersNode}
      handleNoData={
        <div className="flex justify-center">
          <Heading level={2} color="blue">
            {t('ObjectsOfType.NotFound')}
          </Heading>
        </div>
      }
      hasPanel={false}
      emptyFilterText={t('ContentDefinition.FiltersEmptyResult')}
      additionalGridClasses="h-full md:max-h-[calc(100vh-28rem)] px-2
                  gap-5! grid-cols-[repeat(auto-fill,minmax(10rem,1fr))]!"
    />
  );
};

export default PickTypeContentModal;
